import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle, XCircle, Eye, EyeOff, X } from 'lucide-react';
import { Logo } from '../components/Logo';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Banner state
  const [banner, setBanner] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    // Fetch banner
    const fetchBanner = async () => {
      try {
        const res = await axios.get(`${API}/banner`);
        if (res.data.enabled) {
          setBanner(res.data);
        }
      } catch (e) {
        // Ignore banner errors
      }
    };
    fetchBanner();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 4000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const user = await login(email, password);
      
      if (user.status === 'pending') {
        navigate('/aguardando');
      } else if (user.status === 'blocked') {
        navigate('/bloqueado');
      } else if (user.status === 'expired') {
        navigate('/trial-expirado');
      } else {
        showNotification('Login realizado com sucesso!');
        navigate('/');
      }
    } catch (error) {
      showNotification(error.response?.data?.detail || 'Erro ao fazer login', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
          notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
        }`}>
          {notification.type === 'error' ? <XCircle className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
          {notification.message}
        </div>
      )}
      {/* Left Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          <div className="mb-8">
            <Logo size="lg" />
          </div>
          
          <Card className="border-0 shadow-lg">
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl font-bold">Bem-vindo de volta</CardTitle>
              <CardDescription>Entre com suas credenciais para acessar</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="login-email-input"
                    className="h-11"
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Senha</Label>
                    <Link 
                      to="/esqueci-senha" 
                      className="text-xs text-primary hover:underline"
                      data-testid="forgot-password-link"
                    >
                      Esqueci minha senha
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="login-password-input"
                      className="h-11 pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      data-testid="toggle-password"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-11 rounded-full font-medium"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Entrar'
                  )}
                </Button>
              </form>
              
              <div className="mt-6 text-center text-sm text-muted-foreground">
                Não tem uma conta?{' '}
                <Link 
                  to="/cadastro" 
                  className="text-primary font-medium hover:underline"
                  data-testid="register-link"
                >
                  Cadastre-se
                </Link>
              </div>
              
              <div className="mt-4 pt-4 border-t">
                <Link 
                  to="/admin/login" 
                  className="block text-center text-sm text-muted-foreground hover:text-foreground"
                  data-testid="admin-login-link"
                >
                  Acesso Administrador
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Right Side - Visual */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-violet-500 via-purple-500 to-fuchsia-500 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          <svg className="w-full h-full" viewBox="0 0 800 600" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 300Q200 200 400 300T800 300" stroke="white" strokeWidth="60" fill="none"/>
            <path d="M0 400Q200 300 400 400T800 400" stroke="white" strokeWidth="40" fill="none"/>
            <path d="M0 200Q200 100 400 200T800 200" stroke="white" strokeWidth="40" fill="none"/>
          </svg>
        </div>
        <div className="relative z-10 max-w-lg text-center">
          <div className="bg-white/20 backdrop-blur-lg rounded-3xl p-8 text-white">
            <h2 className="text-3xl font-bold mb-4">Capture suas ideias</h2>
            <p className="text-lg text-white/90">
              Grave ou digite suas ideias e organize-as por categorias. 
              Ouça suas anotações a qualquer momento.
            </p>
          </div>
          
          {/* Banner */}
          {banner && !bannerDismissed && (
            <div className="mt-6 relative">
              <button
                onClick={() => setBannerDismissed(true)}
                className="absolute -top-2 -right-2 z-10 w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                title="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
              
              {banner.link ? (
                <a 
                  href={banner.link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="block bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-shadow cursor-pointer"
                >
                  {banner.image_url && (
                    <img 
                      src={banner.image_url} 
                      alt={banner.title || 'Banner'} 
                      className="w-full h-32 object-cover"
                    />
                  )}
                  {(banner.title || banner.description) && (
                    <div className="p-4 text-left">
                      {banner.title && (
                        <h3 className="font-bold text-slate-900">{banner.title}</h3>
                      )}
                      {banner.description && (
                        <p className="text-sm text-slate-600 mt-1">{banner.description}</p>
                      )}
                      <p className="text-xs text-orange-500 mt-2 font-medium">Clique para saber mais →</p>
                    </div>
                  )}
                </a>
              ) : (
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                  {banner.image_url && (
                    <img 
                      src={banner.image_url} 
                      alt={banner.title || 'Banner'} 
                      className="w-full h-32 object-cover"
                    />
                  )}
                  {(banner.title || banner.description) && (
                    <div className="p-4 text-left">
                      {banner.title && (
                        <h3 className="font-bold text-slate-900">{banner.title}</h3>
                      )}
                      {banner.description && (
                        <p className="text-sm text-slate-600 mt-1">{banner.description}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
